import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { apiService } from '../../services/api.service';
import { useTenantStore } from '../../stores/tenant.store';
import { useAccountingPeriodStore } from '../../stores/accounting-period.store';
import { usePermissions } from '../../context/PermissionsContext';
import {
  AccountingEmptyState,
  AccountingErrorPanel,
  AccountingSkeleton,
  AccountingTooltip,
  humanizeAccountingError,
  periodStatusLabel,
  periodStatusTone,
} from './ui';

type DashAlert = {
  id: string;
  severity: 'ERROR' | 'WARNING' | 'INFO';
  title: string;
  detail?: string;
  ctaLabel: string;
  ctaHref: string;
};

type CloseCheck = {
  key: string;
  label: string;
  status: 'ok' | 'warn' | 'fail';
};

type Dash = {
  periodStatus?: string;
  periodStatusLabel?: string;
  period?: {
    id: string | null;
    year: number;
    month: number;
    status: string;
    title: string;
  };
  entity?: { code: string; legalName: string; baseCurrency: string };
  draftCount?: number;
  unmatchedBank?: number;
  openCashSessions?: number;
  opsWithoutJournal?: number;
  pendingAccruals?: number;
  accountingErrorCount?: number;
  resultMtd?: number | string;
  resultYtd?: number | string;
  totalIncomeMtd?: number | string;
  totalExpenseMtd?: number | string;
  netFlowMtd?: number | string | null;
  lastJournalEntry?: {
    id: string;
    entryNumber: number | null;
    status: string;
    at: string;
    entryDate: string;
    description: string;
  } | null;
  alerts?: DashAlert[];
  closeChecks?: CloseCheck[];
  closeProgress?: { done: number; total: number };
  canClose?: boolean;
};

const money = (v: number | string | undefined) =>
  Number(v ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const checkMark = (s: CloseCheck['status']) => {
  if (s === 'ok') return { glyph: '✓', className: 'text-emerald-700' };
  if (s === 'warn') return { glyph: '⚠', className: 'text-amber-700' };
  return { glyph: '✕', className: 'text-red-700' };
};

const alertTone = (sev: DashAlert['severity']) => {
  if (sev === 'ERROR') return 'border-red-200 bg-red-50 text-red-900';
  if (sev === 'WARNING') return 'border-amber-200 bg-amber-50 text-amber-950';
  return 'border-sky-200 bg-sky-50 text-sky-950';
};

export function AccountingHubPage() {
  const legalEntityId = useTenantStore((s) => s.legalEntityId);
  const dateTo = useAccountingPeriodStore((s) => s.dateTo);
  const dateFrom = useAccountingPeriodStore((s) => s.dateFrom);
  const setDateFrom = useAccountingPeriodStore((s) => s.setDateFrom);
  const setDateTo = useAccountingPeriodStore((s) => s.setDateTo);
  const { can, loading: permLoading } = usePermissions();

  const [dash, setDash] = useState<Dash | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [periodEditor, setPeriodEditor] = useState(false);

  const canRead = can('accounting', 'read');

  useEffect(() => {
    if (!legalEntityId || !canRead) {
      setDash(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const d = await apiService.getAccountingDashboard(legalEntityId, dateTo || undefined);
        if (!cancelled) setDash(d);
      } catch (e: any) {
        if (!cancelled) {
          setDash(null);
          setError(humanizeAccountingError(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [legalEntityId, dateTo, canRead, reloadKey]);

  if (permLoading) {
    return <AccountingSkeleton kind="report" />;
  }

  if (!canRead) {
    return (
      <AccountingEmptyState
        title="Sin permisos"
        description="No tenés permiso de lectura sobre Contabilidad. Pedile acceso a un administrador."
      />
    );
  }

  if (!legalEntityId) {
    return (
      <AccountingEmptyState
        title="Sin empresa"
        description="Seleccioná una empresa en la barra superior para ver el estado contable."
      />
    );
  }

  if (loading && !dash) {
    return <AccountingSkeleton kind="report" />;
  }

  if (error && !dash) {
    return (
      <AccountingErrorPanel
        title="No se pudo cargar el dashboard"
        message={error}
        onRetry={() => {
          setError(null);
          setReloadKey((k) => k + 1);
        }}
      />
    );
  }

  const alerts = dash?.alerts ?? [];
  const visibleAlerts = showAllAlerts ? alerts : alerts.slice(0, 3);
  const hiddenAlertCount = Math.max(0, alerts.length - 3);
  const checks = dash?.closeChecks ?? [];
  const progress = dash?.closeProgress ?? { done: 0, total: checks.length || 6 };
  const periodMissing = dash?.periodStatus === 'MISSING' || !dash?.period?.id;

  return (
    <div className="space-y-6">
      {/* Header operativo */}
      <section className="rounded-lg border bg-white p-4 sm:p-5 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {dash?.period?.title
                ? `Período · ${dash.period.title}`
                : 'Período de consulta'}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {dash?.entity
                ? `${dash.entity.code} · ${dash.entity.legalName}`
                : 'Empresa activa'}
              {dash?.entity?.baseCurrency ? ` · ${dash.entity.baseCurrency}` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-semibold tracking-wide ${periodStatusTone(dash?.periodStatus)}`}
            >
              {dash?.periodStatusLabel ?? periodStatusLabel(dash?.periodStatus)}
            </span>
            <button
              type="button"
              onClick={() => setPeriodEditor((v) => !v)}
              className="text-sm px-3 py-1.5 rounded-md border bg-white hover:bg-gray-50"
            >
              Cambiar período
            </button>
          </div>
        </div>

        {periodEditor && (
          <div className="flex flex-wrap gap-3 items-end border-t pt-3">
            <label className="text-sm">
              <span className="block text-xs text-gray-500">Desde</span>
              <input
                type="date"
                className="border rounded-md px-2 py-1.5"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </label>
            <label className="text-sm">
              <span className="block text-xs text-gray-500">Hasta / as of</span>
              <input
                type="date"
                className="border rounded-md px-2 py-1.5"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </label>
            <p className="text-xs text-gray-500 max-w-sm">
              El dashboard resume el mes del “Hasta”. El rango se mantiene al navegar a libros y EEFF.
            </p>
          </div>
        )}

        {periodMissing && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
            No hay un período contable abierto para este mes. Se creará al postear el primer asiento,
            o podés gestionarlo en{' '}
            <Link className="underline font-medium" to="/accounting/close">
              Cierre mensual
            </Link>
            .
          </div>
        )}
      </section>

      {/* Alertas */}
      {alerts.length > 0 && (
        <section className="space-y-2" aria-label="Alertas">
          {visibleAlerts.map((a) => (
            <div
              key={a.id}
              className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border px-4 py-3 ${alertTone(a.severity)}`}
            >
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide opacity-70">
                  {a.severity === 'ERROR' ? 'Error' : a.severity === 'WARNING' ? 'Atención' : 'Info'}
                </div>
                <div className="font-medium text-sm sm:text-base">{a.title}</div>
                {a.detail && <div className="text-sm opacity-80 mt-0.5">{a.detail}</div>}
              </div>
              <Link
                to={a.ctaHref}
                className="shrink-0 inline-flex justify-center px-3 py-2 text-sm font-medium rounded-md bg-white/80 border border-black/10 hover:bg-white"
              >
                {a.ctaLabel}
              </Link>
            </div>
          ))}
          {!showAllAlerts && hiddenAlertCount > 0 && (
            <button
              type="button"
              className="text-sm text-slate-600 underline"
              onClick={() => setShowAllAlerts(true)}
            >
              Ver todos ({alerts.length})
            </button>
          )}
          {showAllAlerts && alerts.length > 3 && (
            <button
              type="button"
              className="text-sm text-slate-600 underline"
              onClick={() => setShowAllAlerts(false)}
            >
              Ver menos
            </button>
          )}
        </section>
      )}

      {/* KPIs financieros */}
      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-2">Resultados</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="rounded-lg border bg-white px-4 py-3">
            <div className="text-xs text-gray-500">
              <AccountingTooltip term="MTD">Resultado del mes</AccountingTooltip>
            </div>
            <div className="text-xl font-semibold text-gray-900 mt-1 tabular-nums">
              {money(dash?.resultMtd)}
            </div>
          </div>
          <div className="rounded-lg border bg-white px-4 py-3">
            <div className="text-xs text-gray-500">
              <AccountingTooltip term="YTD">Resultado acumulado YTD</AccountingTooltip>
            </div>
            <div className="text-xl font-semibold text-gray-900 mt-1 tabular-nums">
              {money(dash?.resultYtd)}
            </div>
          </div>
          <div className="rounded-lg border bg-white px-4 py-3">
            <div className="text-xs text-gray-500">Ingresos del período</div>
            <div className="text-xl font-semibold text-gray-900 mt-1 tabular-nums">
              {money(dash?.totalIncomeMtd)}
            </div>
          </div>
          <div className="rounded-lg border bg-white px-4 py-3">
            <div className="text-xs text-gray-500">Gastos del período</div>
            <div className="text-xl font-semibold text-gray-900 mt-1 tabular-nums">
              {money(dash?.totalExpenseMtd)}
            </div>
          </div>
          <div className="rounded-lg border bg-white px-4 py-3">
            <div className="text-xs text-gray-500">Flujo neto</div>
            <div className="text-xl font-semibold text-gray-900 mt-1 tabular-nums">
              {money(dash?.netFlowMtd ?? 0)}
            </div>
          </div>
        </div>
      </section>

      {/* Estado operativo */}
      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-2">Estado operativo</h2>
        <div className="flex flex-wrap gap-2">
          <OpChip
            to="/accounting/close"
            label="Período"
            value={dash?.periodStatusLabel ?? '—'}
          />
          <OpChip
            to="/accounting/books/journal"
            label="Borradores"
            value={String(dash?.draftCount ?? 0)}
          />
          <OpChip
            to="/bank-reconciliation"
            label="Banco sin conciliar"
            value={String(dash?.unmatchedBank ?? 0)}
          />
          <OpChip
            to="/accounting/close/integrity"
            label="Errores contables"
            value={String(dash?.accountingErrorCount ?? 0)}
          />
          <OpChip
            to="/accounting/books/journal"
            label="Último asiento"
            value={
              dash?.lastJournalEntry
                ? formatWhen(dash.lastJournalEntry.at)
                : 'Sin asientos'
            }
          />
        </div>
      </section>

      {/* Operaciones frecuentes */}
      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-2">¿Qué querés hacer?</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            {
              to: '/accounting/books/journal?action=new',
              title: '+ Nuevo asiento manual',
              desc: 'Cargar un asiento de ajuste o corrección',
            },
            {
              to: '/accounting/books/journal',
              title: 'Ver libro diario',
              desc: 'Asientos del período',
            },
            {
              to: '/accounting/books/ledger',
              title: 'Consultar mayor',
              desc: 'Movimientos por cuenta',
            },
            {
              to: '/accounting/books/trial',
              title: 'Sumas y saldos',
              desc: 'Balance de comprobación',
            },
          ].map((b) => (
            <ActionCard key={b.to} {...b} primary />
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-2">Estados financieros</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { to: '/accounting/statements/income', title: 'Estado de resultados' },
            { to: '/accounting/statements/balance', title: 'Estado patrimonial' },
            { to: '/accounting/statements/cashflow', title: 'Flujo de fondos' },
          ].map((b) => (
            <ActionCard key={b.to} to={b.to} title={b.title} />
          ))}
        </div>
      </section>

      {/* Cierre */}
      <section className="rounded-lg border bg-white p-4 sm:p-5 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Cierre de {dash?.period?.title ?? 'período'}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {progress.done} de {progress.total} controles completos
              {dash?.canClose ? ' · Listo para cerrar' : ''}
            </p>
          </div>
          <Link
            to="/accounting/close"
            className="inline-flex px-4 py-2.5 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
          >
            Continuar cierre
          </Link>
        </div>
        <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
          {checks.map((c) => {
            const m = checkMark(c.status);
            return (
              <li key={c.key} className={`flex items-center gap-2 ${m.className}`}>
                <span className="font-semibold w-4 text-center" aria-hidden>
                  {m.glyph}
                </span>
                <span className="text-gray-800">{c.label}</span>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Herramientas secundarias */}
      <section>
        <h2 className="text-sm font-semibold text-gray-500 mb-2">Herramientas</h2>
        <div className="flex flex-wrap gap-2">
          {[
            { to: '/accounting/tools/imports', title: 'Importar' },
            { to: '/accounting/tools/inflation', title: 'Inflación' },
            { to: '/accounting/tools/fx', title: 'Multimoneda' },
            { to: '/accounting/masters/chart', title: 'Plan de cuentas' },
            { to: '/accounting/masters/mappings', title: 'Mapeos' },
            { to: '/accounting/masters/cost-centers', title: 'Centros de costo' },
          ].map((b) => (
            <Link
              key={b.to}
              to={b.to}
              className="px-3 py-2 text-sm rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            >
              {b.title}
            </Link>
          ))}
        </div>
      </section>

      {/* Relacionado */}
      <section className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 space-y-3">
        <h2 className="text-sm font-semibold text-gray-900">Relacionado</h2>
        <p className="text-sm text-gray-600 max-w-2xl">
          Los asientos de préstamos, cobranzas, caja, bancos y proveedores se generan
          automáticamente. Desde Contabilidad podés revisar, ajustar, analizar y cerrar el período.
        </p>
        <div className="flex flex-wrap gap-2">
          {[
            { to: '/cash', title: 'Caja / Tesorería' },
            { to: '/bank-reconciliation', title: 'Bancos' },
            { to: '/payables', title: 'Proveedores' },
            { to: '/fiscal', title: 'Fiscal / IVA' },
          ].map((b) => (
            <Link
              key={b.to}
              to={b.to}
              className="px-3 py-2 text-sm rounded-md bg-white border text-slate-800 hover:bg-slate-50"
            >
              {b.title} →
            </Link>
          ))}
        </div>
      </section>

      {error && dash && (
        <p className="text-xs text-amber-700">Última carga con aviso: {String(error)}</p>
      )}
    </div>
  );
}

function ActionCard({
  to,
  title,
  desc,
  primary,
}: {
  to: string;
  title: string;
  desc?: string;
  primary?: boolean;
}) {
  return (
    <Link
      to={to}
      className={`block rounded-lg border bg-white p-4 min-h-[4.5rem] hover:border-slate-400 hover:shadow-sm transition-colors ${
        primary ? 'sm:min-h-[5rem]' : ''
      }`}
    >
      <div className="font-medium text-gray-900">{title}</div>
      {desc && <div className="text-sm text-gray-500 mt-1">{desc}</div>}
    </Link>
  );
}

function OpChip({ to, label, value }: { to: string; label: string; value: string }) {
  return (
    <Link
      to={to}
      className="inline-flex flex-col rounded-md border bg-white px-3 py-2 min-w-[7.5rem] hover:border-slate-400"
    >
      <span className="text-[11px] uppercase tracking-wide text-gray-500">{label}</span>
      <span className="text-sm font-semibold text-gray-900 mt-0.5">{value}</span>
    </Link>
  );
}

function formatWhen(iso: string) {
  try {
    return new Date(iso).toLocaleString('es-AR', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}
